import { useEffect, useRef, useState } from "react";
import { toast } from "sonner";
import { AlertTriangle } from "lucide-react";
import Layout from "@/components/layout/Layout";
import api, { formatApiError, mediaUrl } from "@/lib/api";
import { useCart } from "@/context/CartContext";
import { useAuth } from "@/context/AuthContext";
import { captureProof } from "@/lib/proof";
import { money } from "@/lib/gangsheet";
import { useGangSheet } from "@/components/shirts/gang/useGangSheet";
import SheetStage from "@/components/shirts/gang/SheetStage";
import SheetToolbar from "@/components/shirts/gang/SheetToolbar";
import ImagePanel from "@/components/shirts/gang/ImagePanel";
import GangDock from "@/components/shirts/gang/GangDock";
import DesignerBar from "@/components/signs/DesignerBar";
import { PickShopGate } from "@/components/mall/PickShopGate";
import { useMall } from "@/context/MallContext";
import { useShop } from "@/lib/useShop";
import { cardTheme } from "@/lib/mall";

const WALL = "#eef0f3";

/** The gang-sheet builder: one bar, images on the left, the sheet in the middle, options along the bottom. Fits one screen. */
export default function GangSheet() {
    const { addItem, setOpen } = useCart();
    const { user } = useAuth();
    const { store: activeStore } = useMall();
    const { shopSlug: storeSlug } = useShop();
    const theme = cardTheme({ store: { slug: storeSlug } }, activeStore);
    const [store, setStore] = useState(null);
    const sheet = useGangSheet(storeSlug);
    const stageRef = useRef(null);
    const [zoom, setZoom] = useState(1);
    const [adding, setAdding] = useState(false);
    const [layouts, setLayouts] = useState([]);

    useEffect(() => {
        if (!storeSlug) return;
        api.get(`/stores/${storeSlug}`).then(({ data }) => setStore(data.store)).catch(() => { });
    }, [storeSlug]);

    const loadLayouts = () => {
        if (!user) return;
        api.get("/shirts/dtf/layouts").then(({ data }) => setLayouts(data.layouts || [])).catch(() => { });
    };
    useEffect(loadLayouts, [user]); // eslint-disable-line react-hooks/exhaustive-deps

    useEffect(() => {
        const onKey = (e) => {
            if (["INPUT", "TEXTAREA"].includes(e.target.tagName)) return;
            const meta = e.metaKey || e.ctrlKey;
            if (meta && e.key.toLowerCase() === "z") { e.preventDefault(); e.shiftKey ? sheet.redo() : sheet.undo(); return; }
            if (meta && e.key.toLowerCase() === "d") { e.preventDefault(); sheet.selected && sheet.duplicateItem(sheet.selected); return; }
            if (!sheet.selected) return;
            const item = sheet.items.find((i) => i.uid === sheet.selected);
            if (!item) return;
            const step = e.shiftKey ? 1 : 0.1;
            if (e.key === "Backspace" || e.key === "Delete") { e.preventDefault(); sheet.removeItem(sheet.selected); }
            if (e.key === "ArrowLeft") { e.preventDefault(); sheet.updateItem(item.uid, { x: Math.max(0, item.x - step) }); }
            if (e.key === "ArrowRight") { e.preventDefault(); sheet.updateItem(item.uid, { x: item.x + step }); }
            if (e.key === "ArrowUp") { e.preventDefault(); sheet.updateItem(item.uid, { y: Math.max(0, item.y - step) }); }
            if (e.key === "ArrowDown") { e.preventDefault(); sheet.updateItem(item.uid, { y: item.y + step }); }
            if (e.key.toLowerCase() === "r") sheet.rotateItem(item.uid);
        };
        window.addEventListener("keydown", onKey);
        return () => window.removeEventListener("keydown", onKey);
    }, [sheet]);

    const q = sheet.quote;
    const film = sheet.film;
    const warnings = [
        sheet.lengthMode !== "auto" && sheet.contentLen > sheet.lengthIn + 0.01
        && `Sheet too short — your artwork runs ${(sheet.contentLen - sheet.lengthIn).toFixed(1)}" past ${sheet.lengthIn / 12} ft. Switch to Auto or pick a longer sheet.`,
        sheet.conflicts.length && `${sheet.conflicts.length} image${sheet.conflicts.length > 1 ? "s" : ""} too close or overlapping (need ${film.min_spacing_inches}" apart)`,
        sheet.oob.length && `${sheet.oob.length} image${sheet.oob.length > 1 ? "s" : ""} outside the printable area`,
        sheet.lowDpi.length && `${sheet.lowDpi.length} image${sheet.lowDpi.length > 1 ? "s" : ""} under 150 dpi — will look soft`,
    ].filter(Boolean);
    const errors = sheet.items.length
        ? [...(q?.errors || []), sheet.conflicts.length ? "Fix overlapping images" : null, sheet.oob.length ? "Move images inside the sheet" : null]
        : ["Add your images"];
    const blocking = sheet.conflicts.length > 0 || sheet.oob.length > 0 || (q?.errors || []).length > 0 || !sheet.items.length;

    const saveLayout = async (name) => {
        try {
            await api.post("/shirts/dtf/layouts", {
                name: name || `Sheet ${new Date().toLocaleDateString()}`,
                items: sheet.items.map(({ url, ...rest }) => rest),
                length_mode: sheet.lengthMode,
                sheets: sheet.sheets,
            });
            loadLayouts();
            toast.success("Sheet saved to your account");
        } catch (e) { toast.error(formatApiError(e.response?.data?.detail)); }
    };

    const openLayout = (l) => {
        sheet.commit(l.items.map((it) => ({ ...it, url: mediaUrl(it.file?.url) })));
        sheet.setLengthMode(l.length_mode ?? "auto");
        sheet.setSheets(l.sheets || 1);
        toast.success(`Loaded "${l.name}"`);
    };

    const deleteLayout = async (id) => {
        await api.delete(`/shirts/dtf/layouts/${id}`);
        loadLayouts();
    };

    const add = async () => {
        setAdding(true);
        try {
            const proof = await captureProof(stageRef.current, "dtf-gang-sheet", WALL);
            addItem({
                product_slug: "dtf-gang-sheet",
                product_name: "DTF Gang Sheet",
                image: proof ? mediaUrl(proof.url) : "",
                quantity: q.quantity,
                unit_price: q.unit_price,
                line_total: q.line_total,
                summary: `${film.name} · ${film.width_inches}" × ${q.billed_length_in}" · ${q.linear_feet} linear ft · ${sheet.items.length} images · ${sheet.cover}% coverage`,
                store,
                selections: {
                    kind: "dtf",
                    store_slug: storeSlug,
                    length_in: sheet.lengthIn,
                    quantity: sheet.sheets,
                    film_id: sheet.filmId,
                    film_name: film.name,
                    width_in: film.width_inches,
                    spacing_in: film.min_spacing_inches,
                    printable_width_in: film.printable_width_inches,
                    coverage_pct: sheet.cover,
                    layout: sheet.items.map((it) => ({
                        file_id: it.file?.id, name: it.name, x: it.x, y: it.y,
                        w: it.wIn, h: it.hIn, rot: it.rot || 0, dpi: it.px ? Math.floor(it.px.w / it.wIn) : null,
                    })),
                },
                proof,
                artwork: sheet.groups.map((g) => g.file),
            });
            toast.success("Added to cart");
            setOpen(true);
        } catch (e) {
            toast.error(formatApiError(e.response?.data?.detail));
        } finally {
            setAdding(false);
        }
    };

    if (!storeSlug) {
        return <Layout><PickShopGate room="dtf" title="Which shop prints your gang sheet?" to="/shirts/dtf/gang-sheet" /></Layout>;
    }

    const rate = q?.rate_per_foot ? money(q.rate_per_foot) : "$5.00";
    const priceNote = `${film.width_inches}" × ${q?.billed_length_in || 0}" · ${q?.linear_feet || 0} linear ft · ${sheet.items.length} image${sheet.items.length === 1 ? "" : "s"} · ${sheet.sheets} sheet${sheet.sheets > 1 ? "s" : ""}`;

    return (
        <Layout designer>
            <div className="flex flex-col bg-white lg:h-[calc(100vh-105px)] lg:overflow-hidden" data-testid="gang-designer">
                <DesignerBar back="/shirts/dtf" backLabel="DTF Gang Sheet" backTestid="back-to-kinds" prefix="dtf" title="DTF Gang Sheet" titleTestid="gang-sheet-title"
                    description={`${film.name} · ${film.width_inches}" wide · ${film.printable_width_inches}" printable · ${rate}/linear ft · ${film.min_spacing_inches}" between images`}
                    store={store} theme={theme} price={q?.line_total} priceNote={priceNote} errors={errors} sale={q?.sale} gangDtf
                    blocked={adding || blocking} onAdd={add} adding={adding} />

                <div className="grid min-h-0 flex-1 lg:grid-cols-[300px_1fr]">
                    <ImagePanel sheet={sheet} theme={theme} className="order-2 border-t border-zinc-200 lg:order-1 lg:border-r lg:border-t-0" />
                    <div className="relative order-1 h-[52vh] min-h-[360px] lg:order-2 lg:h-auto lg:min-h-0" style={{ background: WALL }} data-testid="sheet-stage-wrap">
                        <SheetToolbar sheet={sheet} zoom={zoom} setZoom={setZoom} />
                        <div ref={stageRef} className="h-full w-full">
                            <SheetStage
                                items={sheet.items} selected={sheet.selected} setSelected={sheet.setSelected}
                                lengthIn={sheet.lengthIn} zoom={zoom} conflicts={sheet.conflicts} oob={sheet.oob} film={film}
                                onDragStart={() => sheet.commit((p) => p)}
                                onLiveChange={(uid, patch) => sheet.updateItem(uid, patch, { history: false })}
                            />
                        </div>
                        {warnings.length > 0 && (
                            <div className="absolute bottom-4 left-1/2 z-10 max-w-[90%] -translate-x-1/2 space-y-1 rounded-2xl border border-amber-300 bg-amber-50/95 px-4 py-2 shadow-lg backdrop-blur" data-testid="sheet-warnings">
                                {warnings.map((w, i) => (
                                    <p key={i} className="flex gap-2 text-xs font-semibold text-amber-800"><AlertTriangle size={13} className="mt-0.5 shrink-0" /> {w}</p>
                                ))}
                            </div>
                        )}
                    </div>
                </div>

                <GangDock sheet={sheet} theme={theme} user={user} layouts={layouts} onSave={saveLayout} onOpen={openLayout} onDelete={deleteLayout} />
            </div>
        </Layout>
    );
}
